import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';

export default function ClientDashboard() {
  const { profile, user } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [clientRecord, setClientRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [message, setMessage] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (user?.id) fetchClientData();
  }, [user]);

  const fetchClientData = async () => {
    setLoading(true);
    try {
      const { data: client } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', user.id)
        .single();

      setClientRecord(client);

      if (client) {
        const [subsRes, txnsRes] = await Promise.all([
          supabase.from('subscriptions')
            .select('*, product:product_id(name, price_amount, billing_interval, billing_type)')
            .eq('client_id', client.id)
            .order('created_at', { ascending: false }),
          supabase.from('transactions')
            .select('*')
            .eq('client_id', client.id)
            .order('created_at', { ascending: false })
            .limit(20)
        ]);
        setSubscriptions(subsRes.data || []);
        setTransactions(txnsRes.data || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePaymentMethod = async () => {
    setPortalLoading(true);
    setMessage('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/client/billing-portal`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error);

      // Redirect to Stripe customer portal
      window.location.href = data.url;
    } catch (err) {
      setMessage('Error: ' + err.message);
      setPortalLoading(false);
    }
  };

  const handlePause = async (sub) => {
    setActionLoading(sub.id + '_pause');
    setMessage('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/client/subscriptions/${sub.id}/pause`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMessage('Subscription paused. You keep access but billing is paused.');
      fetchClientData();
    } catch (err) {
      setMessage('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResume = async (sub) => {
    setActionLoading(sub.id + '_resume');
    setMessage('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/client/subscriptions/${sub.id}/resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMessage('Subscription resumed. Billing will continue on your next scheduled date.');
      fetchClientData();
    } catch (err) {
      setMessage('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSkipNext = async (sub) => {
    if (!window.confirm('Skip your next payment? Your subscription will continue normally after.')) return;
    setActionLoading(sub.id + '_skip');
    setMessage('');
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/client/subscriptions/${sub.id}/skip`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setMessage('Next payment skipped. Your subscription continues normally after.');
      fetchClientData();
    } catch (err) {
      setMessage('Error: ' + err.message);
    } finally {
      setActionLoading(null);
    }
  };

  const fmt = (n) => `$${Number(n || 0).toFixed(2)}`;
  const intervalLabel = { week: 'Weekly', biweek: 'Bi-weekly', month: 'Monthly', year: 'Yearly' };
  const hasActiveSubscription = subscriptions.some(s => s.status === 'active' || s.status === 'paused');
  const hasStripeCustomer = !!clientRecord?.stripe_customer_id;

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--bg-primary)' }}>
      <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
        <div style={{ width: 40, height: 40, border: '3px solid var(--border)', borderTop: '3px solid var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
        Loading your account...
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-primary)', padding: '32px 24px', maxWidth: 720, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'linear-gradient(135deg, var(--accent), var(--accent-dark))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, color: '#0a0a0a', fontSize: 14
            }}>H</div>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>HMA Portal</span>
          </div>
          <h1 style={{ fontSize: 28, color: 'var(--text-primary)', marginBottom: 4 }}>My Account</h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
            {profile?.full_name || clientRecord?.full_name || profile?.email}
          </p>
        </div>
        <button
          onClick={() => supabase.auth.signOut().then(() => window.location.href = '/login')}
          className="btn btn-secondary btn-sm"
        >
          Sign out
        </button>
      </div>

      {message && (
        <div className={`alert ${message.startsWith('Error') ? 'alert-error' : 'alert-success'}`} style={{ marginBottom: 24 }}>
          {message}
        </div>
      )}

      {/* Billing Management */}
      {hasStripeCustomer && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '20px 24px', marginBottom: 28,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12
        }}>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Payment Method</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              Update your card or view billing details
            </div>
          </div>
          <button
            className="btn btn-secondary"
            onClick={handleUpdatePaymentMethod}
            disabled={portalLoading}
          >
            {portalLoading ? 'Opening...' : '💳 Manage Billing'}
          </button>
        </div>
      )}

      {/* Active Subscriptions */}
      <h2 style={{ fontSize: 20, marginBottom: 16 }}>My Subscriptions</h2>

      {subscriptions.length === 0 ? (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '40px 24px', textAlign: 'center',
          color: 'var(--text-muted)', marginBottom: 32
        }}>
          No subscriptions found.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
          {subscriptions.map(sub => {
            const isPaused = sub.status === 'paused' || sub.paused_at;
            const isCancelled = sub.status === 'cancelled';
            return (
              <div key={sub.id} style={{
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 12, padding: '20px 24px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 4 }}>
                      {sub.product?.name || 'Subscription'}
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>
                      {fmt(sub.product?.price_amount)} · {sub.product?.billing_type === 'one_time' ? 'One-time' : intervalLabel[sub.product?.billing_interval]}
                    </div>
                    {sub.current_period_end && !isCancelled && (
                      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                        {isPaused ? 'Billing paused' : `Next billing: ${new Date(sub.current_period_end).toLocaleDateString('en-CA')}`}
                      </div>
                    )}
                  </div>
                  <span className={`badge ${isCancelled ? 'badge-inactive' : isPaused ? 'badge-pending' : 'badge-active'}`}>
                    {isCancelled ? 'Cancelled' : isPaused ? 'Paused' : 'Active'}
                  </span>
                </div>

                {!isCancelled && (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {isPaused ? (
                      <button className="btn btn-primary btn-sm" onClick={() => handleResume(sub)}
                        disabled={actionLoading === sub.id + '_resume'}>
                        {actionLoading === sub.id + '_resume' ? 'Resuming...' : 'Resume Billing'}
                      </button>
                    ) : (
                      <>
                        <button className="btn btn-secondary btn-sm" onClick={() => handlePause(sub)}
                          disabled={actionLoading === sub.id + '_pause'}>
                          {actionLoading === sub.id + '_pause' ? 'Pausing...' : 'Pause Billing'}
                        </button>
                        {sub.stripe_subscription_id && !sub.next_payment_skipped && (
                          <button className="btn btn-secondary btn-sm" onClick={() => handleSkipNext(sub)}
                            disabled={actionLoading === sub.id + '_skip'}>
                            {actionLoading === sub.id + '_skip' ? 'Skipping...' : 'Skip Next Payment'}
                          </button>
                        )}
                        {sub.next_payment_skipped && (
                          <span style={{ fontSize: 13, color: 'var(--warning)', padding: '6px 0' }}>
                            ✓ Next payment skipped
                          </span>
                        )}
                      </>
                    )}
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '6px 0', marginLeft: 4 }}>
                      To cancel, contact your coach.
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Payment History */}
      <h2 style={{ fontSize: 20, marginBottom: 16 }}>Payment History</h2>
      {transactions.length === 0 ? (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 12, padding: '32px 24px', textAlign: 'center', color: 'var(--text-muted)'
        }}>
          No payment history yet.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map(t => (
                <tr key={t.id}>
                  <td>{t.description || 'Payment'}</td>
                  <td style={{ fontWeight: 500 }}>{fmt(t.gross_amount)}</td>
                  <td>
                    <span className={`badge ${t.status === 'completed' ? 'badge-active' : t.status === 'refunded' ? 'badge-inactive' : 'badge-pending'}`}>
                      {t.status}
                    </span>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                    {new Date(t.created_at).toLocaleDateString('en-CA')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
